import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, Wand2, MicOff, LucideSpeech } from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export default function ModifyWithAIModal({ open, onClose, onSubmit, loading }) {
  const [prompt, setPrompt] = useState("");
  const [recording, setRecording] = useState(false);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPrompt("");
      setTimeout(() => textareaRef.current?.focus(), 50);
    } else {
      recognitionRef.current?.stop?.();
      setRecording(false);
    }
  }, [open]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Tu navegador no soporta dictado por voz. Usa Chrome.");
      return;
    }
    const r = new SR();
    r.lang = "es-ES";
    r.continuous = true;
    r.interimResults = true;
    let finalText = prompt;
    r.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalText += " " + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setPrompt((finalText + " " + interim).trim());
    };
    r.onerror = (e) => {
      console.error(e);
      toast.error("Error de reconocimiento de voz");
      setRecording(false);
    };
    r.onend = () => setRecording(false);
    r.start();
    recognitionRef.current = r;
    setRecording(true);
  };

  const stopVoice = () => {
    recognitionRef.current?.stop();
    setRecording(false);
  };

  const handleSubmit = async () => {
    const texto = prompt.trim();
    if (!texto || loading) return;
    await onSubmit(texto);
  };

  const ready = prompt.trim().length > 0 && !loading;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onClose()}>
      <DialogContent className="max-w-xl" data-testid="modify-with-ai-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-blue-600" /> Modificar con IA
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm text-slate-600 leading-relaxed">
            Describe lo que quieres añadir al presupuesto. La IA
            <strong> sólo añadirá partidas nuevas</strong>, sin modificar las existentes.
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
              ¿Qué quieres añadir?
            </Label>
            <div className="relative">
              <Textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ej: Añade aire acondicionado por conductos en cada habitación, y un termo eléctrico de 100L."
                rows={4}
                className="resize-none text-sm pr-14"
                disabled={loading}
                data-testid="modify-with-ai-input"
              />
              <button
                type="button"
                onClick={recording ? stopVoice : startVoice}
                data-testid="modify-with-ai-voice-btn"
                disabled={loading}
                className={`absolute bottom-3 right-3 p-2.5 rounded-full transition-all ${
                  recording
                    ? "bg-red-500 text-white rx-mic-pulse"
                    : "bg-white border border-slate-300 text-slate-600 hover:border-blue-600 hover:text-blue-600"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                aria-label={recording ? "Detener dictado" : "Dictar"}
              >
                {recording ? (
                  <MicOff className="w-4 h-4" />
                ) : (
                  <LucideSpeech className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="text-[11px] text-slate-400 mt-1.5">
              {recording
                ? "Grabando… pulsa para parar."
                : "Pulsa el micrófono para dictar (Chrome)."}
            </div>
          </div>

          <div className="rounded-md bg-blue-50/60 border border-blue-200 px-3 py-2 text-[11px] text-slate-600 leading-relaxed">
            <strong className="text-blue-700">Cómo funciona:</strong> la IA descompondrá tu prompt
            en partidas nuevas, buscará la mejor opción del catálogo CYPE para cada una
            y las añadirá como extras. Podrás editarlas o eliminarlas después.
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="border-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!ready}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="modify-with-ai-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Procesando…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Añadir partidas
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
